import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { criticalThinkingService, ThinkingExercise, ExerciseEvaluation, GenerateExerciseParams } from '../../services/criticalThinkingService';
import PageHeader from '../common/PageHeader';
import './CriticalThinkingPractice.css';

interface CriticalThinkingPracticeProps {
  onToggleSidebar: () => void;
}

type TabType = 'problem' | 'prompts' | 'analysis' | 'evaluation';

const CriticalThinkingPractice: React.FC<CriticalThinkingPracticeProps> = ({ onToggleSidebar }) => {
  const { user } = useAuth();
  const [exercise, setExercise] = useState<ThinkingExercise | null>(null);
  const [userResponse, setUserResponse] = useState('');
  const [evaluation, setEvaluation] = useState<ExerciseEvaluation | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('problem');
  
  // Exercise generation options
  const [exerciseType, setExerciseType] = useState<GenerateExerciseParams['exerciseType']>('critical_thinking');
  const [difficulty, setDifficulty] = useState<GenerateExerciseParams['difficulty']>('medium');
  const [topic, setTopic] = useState('');

  const handleGenerateExercise = async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    setExercise(null);
    setUserResponse('');
    setEvaluation(null);

    try {
      const params: GenerateExerciseParams = {
        exerciseType,
        difficulty,
        topic: topic.trim() || undefined
      };
      
      const newExercise = await criticalThinkingService.generateExercise(params);
      setExercise(newExercise);
      setActiveTab('problem');
    } catch (err: any) {
      setError(err.message || 'Failed to generate exercise. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitResponse = async () => {
    if (!exercise || !userResponse.trim()) {
      setError('Please enter your response before submitting.');
      return;
    }

    if (userResponse.trim().length < 50) {
      setError('Response must be at least 50 characters long.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const evalResult = await criticalThinkingService.submitResponse({
        exerciseId: exercise.exerciseId,
        userResponse: userResponse.trim()
      });
      setEvaluation(evalResult);
      setActiveTab('evaluation');
    } catch (err: any) {
      setError(err.message || 'Failed to submit response. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleNewExercise = () => {
    setExercise(null);
    setUserResponse('');
    setEvaluation(null);
    setError(null);
    setActiveTab('problem');
  };

  const rightActions = exercise ? (
    <button onClick={handleNewExercise} className="critical-thinking-new-btn">
      New Exercise
    </button>
  ) : null;

  return (
    <div className="critical-thinking-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="critical-thinking-header-title">/critical-thinking</h1>}
        rightActions={rightActions}
      />
      
      <div className="critical-thinking-content">
        {error && (
          <div className="critical-thinking-error">
            {error}
          </div>
        )}

        {!exercise ? (
          <div className="critical-thinking-generate-section">
            <div className="critical-thinking-section">
              <h2 className="critical-thinking-section-title">Generate Exercise</h2>
              
              <div className="critical-thinking-form-group">
                <label className="critical-thinking-label">Exercise Type</label>
                <select
                  value={exerciseType}
                  onChange={(e) => setExerciseType(e.target.value as GenerateExerciseParams['exerciseType'])}
                  className="critical-thinking-select"
                  disabled={loading}
                >
                  <option value="critical_thinking">Critical Thinking</option>
                  <option value="logical_reasoning">Logical Reasoning</option>
                  <option value="argument_analysis">Argument Analysis</option>
                  <option value="problem_solving">Problem Solving</option>
                </select>
              </div>

              <div className="critical-thinking-form-group">
                <label className="critical-thinking-label">Difficulty</label>
                <select
                  value={difficulty}
                  onChange={(e) => setDifficulty(e.target.value as GenerateExerciseParams['difficulty'])}
                  className="critical-thinking-select"
                  disabled={loading}
                >
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                </select>
              </div>

              <div className="critical-thinking-form-group">
                <label className="critical-thinking-label">Topic (Optional)</label>
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="e.g., climate change, artificial intelligence..."
                  className="critical-thinking-input"
                  disabled={loading}
                />
              </div>

              <button
                onClick={handleGenerateExercise}
                disabled={loading}
                className="critical-thinking-generate-btn"
              >
                {loading ? 'Generating...' : 'Generate Exercise'}
              </button>
            </div>
          </div>
        ) : (
          <div className="critical-thinking-exercise-section">
            <div className="critical-thinking-tab-content">
              {activeTab === 'problem' && (
                <div className="critical-thinking-section">
                  <h2 className="critical-thinking-section-title">Problem</h2>
                  <div className="critical-thinking-problem">
                    {exercise.problemText}
                  </div>
                </div>
              )}

              {activeTab === 'prompts' && (
                <div className="critical-thinking-section">
                  <h2 className="critical-thinking-section-title">Analysis Prompts</h2>
                  <ol className="critical-thinking-prompts-list">
                    {exercise.prompts.map((prompt, index) => (
                      <li key={index} className="critical-thinking-prompt-item">
                        {prompt}
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {activeTab === 'analysis' && !evaluation && (
                <div className="critical-thinking-section">
                  <h2 className="critical-thinking-section-title">Your Analysis</h2>
                  <textarea
                    value={userResponse}
                    onChange={(e) => setUserResponse(e.target.value)}
                    placeholder="Enter your critical analysis here. Consider the prompts above and provide a thoughtful response..."
                    className="critical-thinking-textarea"
                    rows={12}
                    disabled={loading}
                  />
                  <div className="critical-thinking-char-count">
                    {userResponse.length} characters (minimum 50)
                  </div>
                  <button
                    onClick={handleSubmitResponse}
                    disabled={loading || userResponse.trim().length < 50}
                    className="critical-thinking-submit-btn"
                  >
                    {loading ? 'Evaluating...' : 'Submit for Evaluation'}
                  </button>
                </div>
              )}

              {activeTab === 'evaluation' && evaluation && (
                <div className="critical-thinking-section">
                  <h2 className="critical-thinking-section-title">Evaluation</h2>
                  
                  <div className="critical-thinking-score">
                    <div className="critical-thinking-score-value">{evaluation.score}</div>
                    <div className="critical-thinking-score-label">/ 100</div>
                  </div>

                  <div className="critical-thinking-summary">
                    {evaluation.summary}
                  </div>

                  {evaluation.strengths.length > 0 && (
                    <div className="critical-thinking-feedback-group">
                      <h3 className="critical-thinking-feedback-title">Strengths</h3>
                      <ul className="critical-thinking-feedback-list">
                        {evaluation.strengths.map((strength, index) => (
                          <li key={index}>{strength}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {evaluation.improvements.length > 0 && (
                    <div className="critical-thinking-feedback-group">
                      <h3 className="critical-thinking-feedback-title">Areas for Improvement</h3>
                      <ul className="critical-thinking-feedback-list">
                        {evaluation.improvements.map((improvement, index) => (
                          <li key={index}>{improvement}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {evaluation.detailedFeedback && (
                    <div className="critical-thinking-feedback-group">
                      <h3 className="critical-thinking-feedback-title">Detailed Feedback</h3>
                      <div className="critical-thinking-detailed-feedback">
                        {evaluation.detailedFeedback.split('\n\n').map((paragraph, index) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                    </div>
                  )}

                  <button
                    onClick={handleNewExercise}
                    className="critical-thinking-new-exercise-btn"
                  >
                    Start New Exercise
                  </button>
                </div>
              )}
            </div>

            <div className="critical-thinking-tabs">
              <button
                className={`critical-thinking-tab ${activeTab === 'problem' ? 'active' : ''}`}
                onClick={() => setActiveTab('problem')}
              >
                Problem
              </button>
              <button
                className={`critical-thinking-tab ${activeTab === 'prompts' ? 'active' : ''}`}
                onClick={() => setActiveTab('prompts')}
              >
                Prompts
              </button>
              {!evaluation ? (
                <button
                  className={`critical-thinking-tab ${activeTab === 'analysis' ? 'active' : ''}`}
                  onClick={() => setActiveTab('analysis')}
                >
                  Your Analysis
                </button>
              ) : (
                <button
                  className={`critical-thinking-tab ${activeTab === 'evaluation' ? 'active' : ''}`}
                  onClick={() => setActiveTab('evaluation')}
                >
                  Evaluation
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CriticalThinkingPractice;

